import { apiFetch } from '@/api/request'
import type { ApiResponse } from '@/types/verification'
import type { MallReview } from '@/constants/mallProducts'

/**
 * 함우리몰 후기 · 문의 API.
 * 후기는 구매가 확인된 기관만, 상품문의는 이 상품이 들어 있는 주문을 고른 경우에만 남길 수 있다.
 */

export interface MallQna {
  idx: number
  id: string
  productCode: string
  productName: string
  productImage: string
  author: string
  org: string
  secret: boolean
  question: string
  answer: string
  answeredAt: string
  date: string
  /** 관련 주문 — 본인·판매·관리자 응답에만 실림 */
  orderCode?: string
  orderAt?: string
  orderStatus?: string
  orderSummary?: string
}

export interface MallQnaOrderOption {
  code: string
  orderedAt: string
  status: string
  totalAmount: number
  itemSummary: string
}

/** 문의에 붙은 관련 주문 한 줄 요약 (관리·판매·마이페이지 공통) */
export function formatQnaOrderRef(qna: {
  orderCode?: string
  orderAt?: string
  orderStatus?: string
  orderSummary?: string
}): string {
  const code = String(qna.orderCode || '').trim()
  if (!code) return ''
  const when = String(qna.orderAt || '').slice(0, 10)
  const summary = String(qna.orderSummary || '').trim()
  const parts = [code]
  if (when) parts.push(when)
  if (summary) parts.push(summary)
  return parts.join(' · ')
}

export interface SellerMallReview extends MallReview {
  idx: number
  productCode: string
  productName: string
  productImage: string
  reply: string
  repliedAt: string
  orderCode?: string
  orderAt?: string
  orderStatus?: string
  orderSummary?: string
}

const API_ORIGIN = (process.env.VUE_APP_API_ORIGIN || '').replace(/\/$/, '')
const MALL_BASE = `${API_ORIGIN}/api/mall`
const SELLER_BASE = `${API_ORIGIN}/api/mall/seller`

const fetchOptions: RequestInit = { credentials: 'include' }
const jsonOptions: RequestInit = {
  ...fetchOptions,
  headers: { 'Content-Type': 'application/json' },
}

async function parseJson<T>(response: Response): Promise<ApiResponse<T>> {
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    throw new Error('API 서버에 연결할 수 없습니다. 백엔드와 프론트 dev 서버를 확인해 주세요.')
  }
  const result = (await response.json()) as ApiResponse<T>
  if (!response.ok && !result.message) {
    throw new Error('요청 처리에 실패했습니다.')
  }
  return result
}

function unwrap<T>(result: ApiResponse<T>, fallback: string): T {
  if (!result.success || !result.data) {
    throw new Error(result.message || fallback)
  }
  return result.data
}

/* ---------------- 구매 기관 ---------------- */

export async function fetchMyReviewStatus(productCode: string) {
  const response = await apiFetch(
    `${MALL_BASE}/products/${encodeURIComponent(productCode)}/review-status`,
    fetchOptions,
  )
  const result = await parseJson<{ canReview: boolean; myReview: SellerMallReview | null }>(response)
  return unwrap(result, '후기 정보를 불러오지 못했습니다.')
}

export async function createProductReview(
  productCode: string,
  payload: { rating: number; content: string; image?: string; orderCode?: string },
) {
  const response = await apiFetch(`${MALL_BASE}/products/${encodeURIComponent(productCode)}/reviews`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ review: SellerMallReview }>(response)
  return unwrap(result, '후기를 등록하지 못했습니다.').review
}

export async function uploadMemberImages(files: File[]) {
  const body = new FormData()
  files.forEach((file) => body.append('images', file))
  const response = await apiFetch(`${MALL_BASE}/my/images`, {
    ...fetchOptions,
    method: 'POST',
    body,
  })
  const result = await parseJson<{ images: Array<{ url: string; name: string; size: number }> }>(
    response,
  )
  return unwrap(result, '이미지를 올리지 못했습니다.').images
}

export async function createProductQna(
  productCode: string,
  payload: { question: string; secret?: boolean; orderCode?: string },
) {
  const response = await apiFetch(`${MALL_BASE}/products/${encodeURIComponent(productCode)}/qnas`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify(payload),
  })
  const result = await parseJson<{ qna: MallQna }>(response)
  return unwrap(result, '문의를 등록하지 못했습니다.').qna
}

export async function fetchQnaOrders(productCode: string) {
  const response = await apiFetch(
    `${MALL_BASE}/products/${encodeURIComponent(productCode)}/qna-orders`,
    fetchOptions,
  )
  const result = await parseJson<{ orders: MallQnaOrderOption[] }>(response)
  return unwrap(result, '주문 목록을 불러오지 못했습니다.').orders
}

export async function fetchMyReviews() {
  const response = await apiFetch(`${MALL_BASE}/my/reviews`, fetchOptions)
  const result = await parseJson<{ reviews: SellerMallReview[] }>(response)
  return unwrap(result, '후기 목록을 불러오지 못했습니다.').reviews
}

export async function fetchMyQnas() {
  const response = await apiFetch(`${MALL_BASE}/my/qnas`, fetchOptions)
  const result = await parseJson<{ qnas: MallQna[] }>(response)
  return unwrap(result, '문의 목록을 불러오지 못했습니다.').qnas
}

/* ---------------- 판매업체 ---------------- */

export async function fetchSellerReviews() {
  const response = await apiFetch(`${SELLER_BASE}/reviews`, fetchOptions)
  const result = await parseJson<{ reviews: SellerMallReview[] }>(response)
  return unwrap(result, '후기 목록을 불러오지 못했습니다.').reviews
}

export async function fetchSellerQnas() {
  const response = await apiFetch(`${SELLER_BASE}/qnas`, fetchOptions)
  const result = await parseJson<{ qnas: MallQna[] }>(response)
  return unwrap(result, '문의 목록을 불러오지 못했습니다.').qnas
}

/**
 * 판매업체 본인이 판매한 주문에 걸린 1:1 상담 목록. 관리자가 답변하고 판매업체는 조회만 한다.
 * 기존 판매업체 「리뷰·문의」 화면의 문의 레인에 상품문의와 함께 병합해서 노출한다.
 */
export interface SellerSharedConsult {
  idx: number
  id: string
  accessId: string
  title: string
  orderCode: string
  orgName: string
  orgCode: string
  authorId: string
  content: string
  createdAt: string
  status: 'WAIT' | 'DONE' | string
  responseCount: number
  latestResponse: string
  latestResponseAt: string | null
  latestResponseBy: string
  /** 이 주문에서 판매업체 본인이 판 상품 이름 요약 */
  myItems: string
  /** 관리자가 판매업체에게 답변 위임을 켰는가 */
  sellerReplyAllowed: boolean
  /** 위임을 켠 관리자 ID */
  delegatedBy: string
  /** 위임 상태를 마지막으로 바꾼 시각 */
  delegatedAt: string | null
}

export async function fetchSellerConsults() {
  const response = await apiFetch(`${SELLER_BASE}/consults`, fetchOptions)
  const result = await parseJson<{ consults: SellerSharedConsult[] }>(response)
  return unwrap(result, '1:1 상담 목록을 불러오지 못했습니다.').consults
}

/**
 * 관리자가 위임한 1:1 상담에 판매업체가 답변을 남긴다.
 * 위임되지 않았거나 이 주문의 판매업체가 아니면 서버가 403 을 돌려준다.
 */
export async function answerSellerConsult(id: number, content: string) {
  const response = await apiFetch(`${SELLER_BASE}/consults/${id}/answer`, {
    ...jsonOptions,
    method: 'POST',
    body: JSON.stringify({ content }),
  })
  const result = await parseJson<{ id: number }>(response)
  return unwrap(result, '상담 답변 등록에 실패했습니다.')
}

export async function replySellerReview(idx: number, reply: string) {
  const response = await apiFetch(`${SELLER_BASE}/reviews/${idx}/reply`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ reply }),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '후기 답변을 등록하지 못했습니다.')
  }
}

export async function answerSellerQna(idx: number, answer: string) {
  const response = await apiFetch(`${SELLER_BASE}/qnas/${idx}/answer`, {
    ...jsonOptions,
    method: 'PATCH',
    body: JSON.stringify({ answer }),
  })
  const result = await parseJson<null>(response)
  if (!result.success) {
    throw new Error(result.message || '문의 답변을 등록하지 못했습니다.')
  }
}
